import { ThemedText } from '@/components/themed-text';
import React, { useEffect, useRef, useState } from 'react';
import { Platform, View } from 'react-native';
import type { SegmentMockKind, SegmentStep } from '@/content/seo/segments';
import { BookingMock, ChargesMock, ChatMock, MockData, PhoneFrame } from './phone-mocks';
import { Reveal, SEO_EASE, SectionTitle, SeoSection } from './seo-blocks';

type ClientWord = 'cliente' | 'aluno' | 'paciente';

const MOCKS: Record<SegmentMockKind, typeof BookingMock> = {
  booking: BookingMock,
  chat: ChatMock,
  charges: ChargesMock,
};

/** Passos padrão. O lembrete do horário é só texto e é dos planos pagos. */
const defaultSteps = (client: ClientWord): SegmentStep[] => [
  { title: `O ${client} marca pelo link`, sub: 'Só horários livres. Sem baixar app.', mock: 'booking' },
  { title: 'O lembrete sai sozinho', sub: `Um dia antes, no WhatsApp do ${client} (planos pagos).`, mock: 'chat' },
  { title: 'Você vê quem pagou', sub: 'Recebido e a receber numa tela.', mock: 'charges' },
];

/**
 * "Como funciona" em 3 passos (próprios do ramo quando a página passa `steps`). No desktop o celular fica parado ao lado e
 * troca de tela conforme o passo em foco; no celular cada passo traz o seu.
 */
export function HowItWorks({
  mock,
  client,
  steps: customSteps,
  title = 'Como funciona em 3 passos?',
}: {
  mock: MockData;
  client: ClientWord;
  /** Passos próprios do ramo; sem isso valem os 3 padrão. */
  steps?: SegmentStep[];
  /** Título da seção, em pergunta. */
  title?: string;
}) {
  const steps = (customSteps ?? defaultSteps(client)).map((step) => ({ ...step, Mock: MOCKS[step.mock] }));
  const [active, setActive] = useState(0);
  const stepRefs = useRef<(View | null)[]>([]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof IntersectionObserver === 'undefined') return;
    const observers = stepRefs.current.map((node, index) => {
      const el = node as unknown as HTMLElement | null;
      if (!el) return null;
      const io = new IntersectionObserver(([entry]) => entry.isIntersecting && setActive(index), {
        rootMargin: '-45% 0px -45% 0px',
      });
      io.observe(el);
      return io;
    });
    return () => observers.forEach((io) => io?.disconnect());
  }, []);

  return (
    <SeoSection>
      <View className="gap-12 md:flex-row md:gap-24">
        <View className="hidden flex-1 items-center md:flex" style={Platform.OS === 'web' ? ({ position: 'sticky', top: 112, alignSelf: 'flex-start' } as object) : undefined}>
          <PhoneFrame>
            {steps.map(({ Mock }, index) => (
              <View
                key={index}
                className="absolute inset-0"
                style={{
                  opacity: active === index ? 1 : 0,
                  ...(Platform.OS === 'web'
                    ? ({ transitionProperty: 'opacity', transitionDuration: '700ms', transitionTimingFunction: SEO_EASE } as object)
                    : null),
                }}
              >
                <Mock mock={mock} />
              </View>
            ))}
          </PhoneFrame>
        </View>

        <View className="flex-1">
          <SectionTitle eyebrow="Como funciona" title={title} />
          {steps.map(({ title, sub, Mock }, index) => (
            <View
              key={title}
              ref={(node) => {
                stepRefs.current[index] = node;
              }}
              className="mb-12 gap-3 md:mb-0 md:min-h-[560px] md:justify-center"
            >
              <Reveal className="gap-3">
                <View className="h-9 w-9 items-center justify-center rounded-full bg-primary">
                  <ThemedText className="font-bold text-sm text-white">{index + 1}</ThemedText>
                </View>
                <ThemedText role="heading" aria-level={3} className="font-bold text-2xl tracking-tight md:text-3xl">
                  {title}
                </ThemedText>
                <ThemedText className="text-base text-neutral-500">{sub}</ThemedText>
              </Reveal>
              <View className="mt-3 h-[420px] items-center overflow-hidden md:hidden">
                <PhoneFrame>
                  <Mock mock={mock} />
                </PhoneFrame>
              </View>
            </View>
          ))}
        </View>
      </View>
    </SeoSection>
  );
}
